import React, { useState, useEffect } from 'react';
import { 
  Download, 
  Filter, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Copy,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import './PaymentHistory.css';

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [filters.status, pagination.page]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const skip = (pagination.page - 1) * pagination.limit;
      
      let url = `${BACKEND_URL}/api/payments?limit=${pagination.limit}&skip=${skip}`;
      if (filters.status !== 'all') url += `&status=${filters.status}`;
      if (filters.dateFrom) url += `&date_from=${filters.dateFrom}`;
      if (filters.dateTo) url += `&date_to=${filters.dateTo}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/payments/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${BACKEND_URL}/api/payments/export/csv`;
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `payments_export_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Payments exported successfully!');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export payments');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { icon: CheckCircle, color: '#22c55e', label: 'Completed' },
      finished: { icon: CheckCircle, color: '#22c55e', label: 'Completed' },
      paid: { icon: CheckCircle, color: '#22c55e', label: 'Paid' },
      pending: { icon: Clock, color: '#f59e0b', label: 'Pending' },
      waiting: { icon: Clock, color: '#f59e0b', label: 'Waiting' },
      confirming: { icon: Clock, color: '#3b82f6', label: 'Confirming' },
      expired: { icon: XCircle, color: '#ef4444', label: 'Expired' },
      failed: { icon: AlertCircle, color: '#ef4444', label: 'Failed' }
    };
    
    const config = statusConfig[status] || { icon: AlertCircle, color: '#6b7280', label: status };
    const Icon = config.icon;
    
    return (
      <span className="status-badge" style={{ color: config.color, borderColor: config.color }}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Filter payments by search term
  const filteredPayments = payments.filter(p => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      p.id?.toLowerCase().includes(searchLower) ||
      p.description?.toLowerCase().includes(searchLower) ||
      p.customer_email?.toLowerCase().includes(searchLower) ||
      p.pay_currency?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="payment-history" data-testid="payment-history">
      {/* Stats Cards */}
      {stats && (
        <div className="stats-summary" data-testid="stats-summary">
          <div className="stat-card today">
            <div className="stat-label">Today</div>
            <div className="stat-value">${stats.today?.revenue?.toFixed(2) || '0.00'}</div>
            <div className="stat-count">{stats.today?.payment_count || 0} payments</div>
          </div>
          <div className="stat-card week">
            <div className="stat-label">This Week</div>
            <div className="stat-value">${stats.this_week?.revenue?.toFixed(2) || '0.00'}</div>
            <div className="stat-count">{stats.this_week?.payment_count || 0} payments</div>
          </div>
          <div className="stat-card month">
            <div className="stat-label">This Month</div>
            <div className="stat-value">${stats.this_month?.revenue?.toFixed(2) || '0.00'}</div>
            <div className="stat-count">{stats.this_month?.payment_count || 0} payments</div>
          </div>
          <div className="stat-card total">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">${stats.total?.net_revenue?.toFixed(2) || '0.00'}</div>
            <div className="stat-count">{stats.total?.payment_count || 0} total payments</div>
          </div>
        </div>
      )}

      {/* Filters Bar */}
      <div className="filters-bar" data-testid="filters-bar">
        <div className="filters-left">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search payments..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="search-input"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
          </select>
          
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            className="date-input"
            placeholder="From"
            data-testid="date-from"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            className="date-input"
            placeholder="To"
            data-testid="date-to"
          />
          
          <button 
            className="btn-filter"
            onClick={fetchPayments}
            data-testid="apply-filters-btn"
          >
            <Filter size={16} /> Apply
          </button>
        </div>
        
        <div className="filters-right">
          <button 
            className="btn-export"
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Payments Table */}
      <div className="table-container" data-testid="payments-table">
        {loading ? (
          <div className="loading-state">Loading payments...</div>
        ) : filteredPayments.length === 0 ? (
          <div className="empty-state">
            <Clock size={48} />
            <h3>No payments found</h3>
            <p>Payments will appear here once customers start paying</p>
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Payment ID</th>
                  <th>Amount</th>
                  <th>Crypto</th>
                  <th>Status</th>
                  <th>Fee</th>
                  <th>Net</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} data-testid={`payment-row-${payment.id}`}>
                    <td className="date-cell">
                      {new Date(payment.created_at).toLocaleDateString()}
                      <span className="time-text">
                        {new Date(payment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="id-cell">
                      <code>{payment.id?.slice(0, 8)}...</code>
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(payment.id)}
                        title="Copy ID"
                      >
                        <Copy size={12} />
                      </button>
                    </td>
                    <td className="amount-cell">
                      ${payment.price_amount?.toFixed(2)}
                      <span className="currency-text">{payment.price_currency?.toUpperCase()}</span>
                    </td>
                    <td className="crypto-cell">
                      {payment.pay_amount?.toFixed(8)}
                      <span className="crypto-currency">{payment.pay_currency?.toUpperCase()}</span>
                    </td>
                    <td>{getStatusBadge(payment.payment_status)}</td>
                    <td className="fee-cell">
                      ${payment.platform_fee_amount?.toFixed(2)}
                      <span className="fee-percent">({payment.platform_fee_percentage}%)</span>
                    </td>
                    <td className="net-cell">
                      ${payment.merchant_net_amount?.toFixed(2)}
                    </td>
                    <td className="actions-cell">
                      {payment.payment_url && (
                        <a 
                          href={payment.payment_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="action-link"
                          title="View Payment"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination" data-testid="pagination">
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {totalPages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={pagination.page === totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
